import { useCallback, useContext, useState } from "react";

import { useAddressBookItems } from "~/features/address-book/hooks/use-address-book.ts";
import { initVal } from "~/features/quote/components/quote-details/constants/quote-detail-constants.ts";
import type { SelectedAddressBookItems } from "~/features/quote/context/quote-address-context.tsx";
import { useQuoteAddressContext } from "~/features/quote/context/quote-address-context.tsx";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { applyTemplateToQuoteDetails } from "~/features/quote/utils/apply-template-to-quote-details.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useIsVendor } from "~/shared/hooks/use-is-vendor.ts";
import type { Accessorial } from "~/shared/schemas/accessorial-schema.ts";
import type { TemplateGridItem } from "~/shared/schemas/template-schema.ts";
import type { QuoteDetailsPageLoadData } from "~/shared/types/hooks-types.ts";
import type { QuoteStateType } from "~/shared/types/quote-details-types.ts";
import {
	ddUseSavedTemplate,
	toDdWorkflow,
} from "~/shared/utils/datadog-actions.ts";

type UseTemplateModalsArgs = {
	pageData: QuoteDetailsPageLoadData | undefined;
	allAccessorials: Accessorial[] | undefined;
	reapplyDefaults: () => void;
};

export function useTemplateModals({
	pageData,
	allAccessorials,
	reapplyDefaults,
}: UseTemplateModalsArgs) {
	const { quoteState, setQuoteState, workflow } =
		useContext(QuoteDetailsContext);
	const {
		resetSelectedAddressBookItems,
		selectedAddressBookItems,
		setSelectedAddressBookItem,
	} = useQuoteAddressContext();

	// Customer's current saved addresses, used to re-verify a template's stored
	// addressBookItemId on apply (cached — already fetched for the page).
	// Templates and the address book are unavailable to vendors, so the lookup
	// must be skipped for them (the endpoint returns 403).
	const { isInternal } = useDeploymentType();
	const { isVendor } = useIsVendor();
	const customerId = quoteState.customer?.id ?? 0;
	const { data: addressBook } = useAddressBookItems(
		{ customerId },
		customerId > 0 && !isVendor,
		isInternal,
	);

	const [showTemplatesModal, setShowTemplatesModal] = useState(false);
	const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
	const [saveTemplateQuoteSnapshot, setSaveTemplateQuoteSnapshot] = useState<
		QuoteStateType | undefined
	>(undefined);
	const [saveTemplateAddressSnapshot, setSaveTemplateAddressSnapshot] =
		useState<SelectedAddressBookItems | undefined>(undefined);

	const toggleTemplatesModal = useCallback(() => {
		setShowTemplatesModal((prev) => !prev);
	}, []);

	const handleTemplateSelect = useCallback(
		(template: TemplateGridItem) => {
			const applied = applyTemplateToQuoteDetails({
				template,
				pageData,
				allAccessorials,
				addressBook,
				resetSelectedAddressBookItems,
				setSelectedAddressBookItem,
				setQuoteState,
				onApplied: () => {
					setShowTemplatesModal(false);
					ddUseSavedTemplate(toDdWorkflow(workflow));
				},
			});

			if (!applied) {
				console.error("Failed to apply template data");
			}
		},
		[
			pageData,
			allAccessorials,
			addressBook,
			resetSelectedAddressBookItems,
			setSelectedAddressBookItem,
			setQuoteState,
			workflow,
		],
	);

	const handleApplySavedTemplate = useCallback(
		(template: TemplateGridItem) => {
			applyTemplateToQuoteDetails({
				template,
				pageData,
				allAccessorials,
				addressBook,
				resetSelectedAddressBookItems,
				setSelectedAddressBookItem,
				setQuoteState,
				onApplied: () => {
					setShowSaveTemplateModal(false);
					ddUseSavedTemplate(toDdWorkflow(workflow));
				},
			});
		},
		[
			allAccessorials,
			addressBook,
			pageData,
			resetSelectedAddressBookItems,
			setQuoteState,
			setSelectedAddressBookItem,
			workflow,
		],
	);

	const handleClearTemplate = useCallback(() => {
		resetSelectedAddressBookItems();
		setQuoteState((prevState) => ({
			...initVal,
			id: prevState.id,
			customer: prevState.customer,
			customerDetails: prevState.customerDetails,
			customerAr: prevState.customerAr,
			customerCredits: prevState.customerCredits,
			carrierPrices: prevState.carrierPrices,
			selectedCarrierPriceId: prevState.selectedCarrierPriceId,
			eTag: prevState.eTag,
			createdDate: prevState.createdDate,
			expirationDate: prevState.expirationDate,
			publicGuid: prevState.publicGuid,
			measuringType: prevState.measuringType,
		}));
		reapplyDefaults();
	}, [resetSelectedAddressBookItems, setQuoteState, reapplyDefaults]);

	const handleOpenSaveTemplateModal = useCallback(() => {
		setSaveTemplateQuoteSnapshot(structuredClone(quoteState));
		setSaveTemplateAddressSnapshot(structuredClone(selectedAddressBookItems));
		setShowSaveTemplateModal(true);
	}, [quoteState, selectedAddressBookItems]);

	const handleCloseSaveTemplateModal = useCallback(() => {
		setShowSaveTemplateModal(false);
	}, []);

	return {
		showTemplatesModal,
		toggleTemplatesModal,
		handleTemplateSelect,
		showSaveTemplateModal,
		saveTemplateQuoteSnapshot,
		saveTemplateAddressSnapshot,
		handleOpenSaveTemplateModal,
		handleCloseSaveTemplateModal,
		handleApplySavedTemplate,
		handleClearTemplate,
	};
}
